
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useActiveAffiliates, useAssignUserToAffiliate } from '@/hooks/useUserAssignment';
import { UnassignedUser } from '@/services/userAssignmentService';

interface AssignReferralDialogProps {
  user: UnassignedUser | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AssignReferralDialog = ({ user, open, onOpenChange }: AssignReferralDialogProps) => {
  const [selectedAffiliateId, setSelectedAffiliateId] = useState<string>('');
  const { data: affiliates, isLoading: loadingAffiliates } = useActiveAffiliates({ enabled: open });
  const assignMutation = useAssignUserToAffiliate();

  const handleAssign = async () => {
    if (!user || !selectedAffiliateId) return;

    const selectedAffiliate = affiliates?.find(a => a.id === selectedAffiliateId);
    if (!selectedAffiliate) return;

    try {
      await assignMutation.mutateAsync({
        userId: user.id,
        affiliateId: selectedAffiliateId,
        referralCode: selectedAffiliate.referral_code
      });
      onOpenChange(false);
      setSelectedAffiliateId('');
    } catch (error) {
      console.error('Error assigning user:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Assign User to Affiliate</DialogTitle>
          <DialogDescription>
            Assign {user?.masked_name} ({user?.masked_email}) to an affiliate partner.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="affiliate" className="text-right">
              Affiliate
            </Label>
            <div className="col-span-3">
              <Select value={selectedAffiliateId} onValueChange={setSelectedAffiliateId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select an affiliate" />
                </SelectTrigger>
                <SelectContent>
                  {loadingAffiliates ? (
                    <SelectItem value="loading" disabled>Loading...</SelectItem>
                  ) : (
                    affiliates?.map((affiliate) => (
                      <SelectItem key={affiliate.id} value={affiliate.id}>
                        {affiliate.name} ({affiliate.referral_code})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleAssign}
            disabled={!selectedAffiliateId || assignMutation.isPending}
          >
            {assignMutation.isPending ? 'Assigning...' : 'Assign'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
